import { useState } from "react";
import { allTags } from "@/mocks/movies";

export interface FilterState {
  tags: string[];
  yearFrom: number | null;
  yearTo: number | null;
  sort: "hot" | "rating" | "new" | "old";
}

interface FilterBarProps {
  value: FilterState;
  onChange: (next: FilterState) => void;
  onReset: () => void;
}

const sortOptions: { value: FilterState["sort"]; label: string }[] = [
  { value: "hot", label: "热门度" },
  { value: "rating", label: "评分最高" },
  { value: "new", label: "最新上映" },
  { value: "old", label: "最早上映" },
];

const yearRange = [2024, 2023, 2022, 2021];

export default function FilterBar({ value, onChange, onReset }: FilterBarProps) {
  const [mobileOpen, setMobileOpen] = useState(false);

  const toggleTag = (tag: string) => {
    const next = value.tags.includes(tag)
      ? value.tags.filter((t) => t !== tag)
      : [...value.tags, tag];
    onChange({ ...value, tags: next });
  };

  const controls = (
    <div className="flex flex-col gap-5">
      <div>
        <span className="text-xs font-semibold uppercase tracking-wide text-foreground-500">类型标签</span>
        <div className="mt-3 flex flex-wrap gap-2">
          {allTags.map((tag) => {
            const active = value.tags.includes(tag);
            return (
              <button
                key={tag}
                type="button"
                onClick={() => toggleTag(tag)}
                className={`whitespace-nowrap rounded-full border px-3 py-1 text-sm transition-colors ${
                  active
                    ? "border-accent-400 bg-accent-100 text-accent-900"
                    : "border-background-300/60 bg-background-50 text-foreground-700 hover:border-accent-300"
                }`}
              >
                {tag}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <span className="text-xs font-semibold uppercase tracking-wide text-foreground-500">年份</span>
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => onChange({ ...value, yearFrom: null, yearTo: null })}
            className={`whitespace-nowrap rounded-full border px-3 py-1 text-sm transition-colors ${
              value.yearFrom === null && value.yearTo === null
                ? "border-primary-400 bg-primary-100 text-primary-700"
                : "border-background-300/60 bg-background-50 text-foreground-700 hover:border-primary-300"
            }`}
          >
            不限
          </button>
          {yearRange.map((y) => {
            const active = value.yearFrom === y && value.yearTo === y;
            return (
              <button
                key={y}
                type="button"
                onClick={() => onChange({ ...value, yearFrom: y, yearTo: y })}
                className={`whitespace-nowrap rounded-full border px-3 py-1 text-sm transition-colors ${
                  active
                    ? "border-primary-400 bg-primary-100 text-primary-700"
                    : "border-background-300/60 bg-background-50 text-foreground-700 hover:border-primary-300"
                }`}
              >
                {y}
              </button>
            );
          })}
          <button
            type="button"
            onClick={() => onChange({ ...value, yearFrom: 2023, yearTo: 2024 })}
            className={`whitespace-nowrap rounded-full border px-3 py-1 text-sm transition-colors ${
              value.yearFrom === 2023 && value.yearTo === 2024
                ? "border-primary-400 bg-primary-100 text-primary-700"
                : "border-background-300/60 bg-background-50 text-foreground-700 hover:border-primary-300"
            }`}
          >
            2023-2024
          </button>
        </div>
      </div>

      <div>
        <span className="text-xs font-semibold uppercase tracking-wide text-foreground-500">排序</span>
        <div className="mt-3 flex flex-wrap gap-2">
          {sortOptions.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => onChange({ ...value, sort: opt.value })}
              className={`whitespace-nowrap rounded-full border px-3 py-1 text-sm transition-colors ${
                value.sort === opt.value
                  ? "border-secondary-400 bg-secondary-100 text-secondary-900"
                  : "border-background-300/60 bg-background-50 text-foreground-700 hover:border-secondary-300"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <button
        type="button"
        onClick={onReset}
        className="inline-flex w-fit items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300/60 px-4 py-2 text-sm font-medium text-foreground-700 transition-colors hover:border-accent-300 hover:text-accent-600"
      >
        <i className="ri-refresh-line"></i> 重置筛选
      </button>
    </div>
  );

  return (
    <div className="rounded-lg border border-background-200/70 bg-background-50 p-5 md:p-6">
      <div className="mb-4 flex items-center justify-between md:hidden">
        <button
          type="button"
          onClick={() => setMobileOpen((v) => !v)}
          className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-semibold text-background-50"
        >
          <i className="ri-filter-3-line"></i> 筛选条件
        </button>
        <span className="text-xs text-foreground-500">
          已选 {value.tags.length} 个标签
        </span>
      </div>
      <div className={`${mobileOpen ? "block" : "hidden"} md:block`}>{controls}</div>
    </div>
  );
}